import { useApolloClient } from '@apollo/client'
import { useFragment } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import {
  MarkUploadDocumentTaskAsDoneDocument,
  RemoveUploadDocumentTaskFileDocument,
  TaskActivityDocument,
  UploadDocumentTaskContentFragmentDoc,
  UploadDocumentTaskFilesDocument,
} from 'generated/resources/graphql'

import DropZone from 'modules/core/components/DropZone'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import TaskFooter from 'modules/inbox_tasks/activities/Task/Footer'
import { useEmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import {
  TaskCustomComponent,
  TaskDefinition,
  TaskFooterComponent,
} from 'modules/inbox_tasks/lib/abstraction'
import ProcurementPurchaseRequestTaskContent from 'modules/inbox_tasks/tasks/approval/ProcurementPurchaseRequest'
import DropItem from 'modules/procurement/components/DropItem'

type State = {
  uploadedFiles?: string[]
}

const UploadDocumentTaskContent: TaskCustomComponent<'ProcurementUploadDocumentTask', State> = ({
  taskable,
  taskId,
}) => {
  // Fetch task content
  const { data: taskData } = useFragment({
    fragment: UploadDocumentTaskContentFragmentDoc,
    fragmentName: 'UploadDocumentTaskContent',
    from: {
      __typename: 'TasksInboxTask',
      id: taskId,
    },
  })

  const apolloClient = useApolloClient()

  const purchaseRequest = taskable?.purchaseRequest
  const owner = purchaseRequest?.requesterEmployee?.employeeName ?? null

  const requesterContent = useEmployeeTableViewRow({ employee: owner, title: 'Requester' })

  const [uploadFiles, { loading: isUploading }] = useMutation(UploadDocumentTaskFilesDocument, {
    extractorFn: (data) => data.procurementMutations.uploadProcurementUploadDocumentTask,
  })

  const [removeFile, { loading: isRemovingFile }] = useMutation(
    RemoveUploadDocumentTaskFileDocument,
    {
      extractorFn: (data) => data.procurementMutations.removeFileProcurementUploadDocumentTask,
    }
  )

  const handleOnDrop = useUnsafeCallback(
    async (files: File[]) => {
      if (!taskable?.id || files.length === 0) return

      const result = await uploadFiles({
        variables: {
          files,
          uploadDocumentTaskId: taskable.id,
        },
      })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        // Refetch task data to show newly uploaded files
        apolloClient.refetchQueries({
          include: [TaskActivityDocument],
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'tasks.types.procurement_request_document.upload_document.errors.upload_error'
          ),
        })
      }
    },
    [taskable?.id, uploadFiles, apolloClient]
  )

  const handleFileRemove = useUnsafeCallback(
    async (fileId: number) => {
      if (!taskable?.id || !fileId) return

      const result = await removeFile({
        variables: {
          fileId: fileId,
          uploadDocumentTaskId: taskable.id,
        },
      })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        apolloClient.refetchQueries({
          include: [TaskActivityDocument],
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'tasks.types.procurement_request_document.upload_document.errors.remove_file_failed'
          ),
        })
      }

      return handled.success
    },
    [taskable?.id, removeFile, apolloClient]
  )

  if (!taskable) return null
  return (
    <Box gap="s12">
      <ProcurementPurchaseRequestTaskContent
        id={purchaseRequest?.purchaseRequestId ?? 0}
        requesterContent={requesterContent}
      />
      <Box gap="s12">
        <Text.Header3>
          {i18n.t('tasks.types.procurement_request_document.required_action_text_label')}
        </Text.Header3>
        {taskData?.content && (
          <Box flexDirection="column" gap="s4">
            <RichTextDisplay content={taskData.content} className="text-f1-foreground-secondary" />
          </Box>
        )}

        <DropZone
          onDrop={handleOnDrop}
          accept={{ 'application/pdf': [], 'image/jpeg': [], 'image/png': [] }}
          multiple
          loading={isUploading}
          placeholder={i18n.t('tasks.types.procurement_request_document.dropzone_placeholder')}
        ></DropZone>
      </Box>
      {taskable?.files && taskable.files.length > 0 && (
        <Box flexDirection="column" gap="s8">
          {taskable.files.map((file) => {
            return (
              <DropItem
                key={file.fileId}
                file={{ ...file, id: file.fileId }}
                onRemove={() => handleFileRemove(file.fileId)}
              />
            )
          })}
        </Box>
      )}
    </Box>
  )
}

const UploadDocumentTaskFooter: TaskFooterComponent<'ProcurementUploadDocumentTask', State> = ({
  taskId,
  taskable,
  taskKind,
}) => {
  const apolloClient = useApolloClient()
  const { employee } = useCurrentSession()

  const [markAsDone, { loading: isMarkingAsDone }] = useMutation(
    MarkUploadDocumentTaskAsDoneDocument,
    {
      extractorFn: (data) => data.procurementMutations.markAsDoneProcurementUploadDocumentTask,
    }
  )

  const handleMarkAsDone = useUnsafeCallback(async () => {
    if (!taskable?.id || !employee?.id) {
      return false
    }

    const hasFiles = taskable.files && taskable.files.length > 0
    if (!hasFiles) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.errors.file_not_attached'
        ),
      })
      return false
    }

    const result = await markAsDone({
      variables: {
        uploadDocumentTaskId: taskable.id,
        completedById: employee.id,
      },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.success.mark_as_done'
        ),
      })

      apolloClient.refetchQueries({
        include: [TaskActivityDocument],
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.errors.mark_as_done_failed'
        ),
      })
    }

    return handled.success
  }, [taskable, employee?.id, markAsDone, apolloClient, taskId])

  if (!taskable?.id) {
    return null
  }

  const hasFiles = taskable.files && taskable.files.length > 0
  const disableMarkAsDone = !hasFiles || isMarkingAsDone

  return (
    <TaskFooter
      taskId={taskId}
      onClickPrimaryAction={handleMarkAsDone}
      actionsDisabled={disableMarkAsDone}
      taskKind={taskKind}
    />
  )
}

export const UploadDocumentTaskDefinition: TaskDefinition<'ProcurementUploadDocumentTask', State> =
  {
    taskableTypename: 'ProcurementUploadDocumentTask',
    initialState: {},
    component: UploadDocumentTaskContent,
    footer: UploadDocumentTaskFooter,
    getTitle: ({ info }) => {
      const description = info.purchaseRequest?.description

      if (!description) {
        return ''
      }

      return `${description}`
    },
    getMetadata: ({ info }) => {
      return [info.purchaseRequest?.description || '']
    },
  }
